import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, RefreshCw, Smartphone, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR } from "../lib/format";
import { api } from "../lib/api";

const POLL_MS = 3000;
const MAX_POLLS = 60; // 3 minuten
const MANUAL_FALLBACK_AFTER_POLLS = 5; // toon handmatige knop na ~15s als scope niet beschikbaar

export default function KassaSumUpSoloPoll() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [polls, setPolls] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [scopeMissing, setScopeMissing] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [redirectIn, setRedirectIn] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    let alive = true;
    let timer = null;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/sumup/solo/${orderId}/status`);
        if (!alive) return;
        setOrder(data.order);
        if (data.scope_missing) setScopeMissing(true);
        pollsRef.current += 1;
        setPolls(pollsRef.current);
        if (
          data.order?.betaal_status === "betaald" ||
          data.order?.betaal_status === "mislukt"
        ) {
          if (data.order.betaal_status === "betaald") {
            toast.success(`Order ${data.order.ordernummer} betaald via Solo`);
            sessionStorage.removeItem("teller_sumup_pending");
          }
          return; // stop polling
        }
        if (pollsRef.current >= MAX_POLLS) return;
        timer = setTimeout(poll, POLL_MS);
      } catch (e) {
        if (alive) timer = setTimeout(poll, POLL_MS * 2);
      } finally {
        if (alive) setRefreshing(false);
      }
    };

    poll();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  // Auto-redirect na success
  useEffect(() => {
    if (order?.betaal_status !== "betaald") return;
    setRedirectIn(4);
    const i = setInterval(() => {
      setRedirectIn((v) => {
        if (v == null) return null;
        if (v <= 1) {
          clearInterval(i);
          navigate("/kassa");
          return 0;
        }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [order?.betaal_status, navigate]);

  const manualFinalize = async (result) => {
    if (result === "betaald") {
      const ok = window.confirm("Weet je zeker dat de Solo-terminal 'Goedgekeurd' liet zien?");
      if (!ok) return;
    }
    setManualBusy(true);
    try {
      const { data } = await api.post(`/payments/sumup/solo/${orderId}/finalize`, {
        result,
        reason: "handmatig bevestigd door kassa",
      });
      setOrder(data.order);
      if (result === "betaald") {
        toast.success(`Order ${data.order.ordernummer} handmatig gefinaliseerd`);
        sessionStorage.removeItem("teller_sumup_pending");
      } else {
        toast.info("Order gemarkeerd als mislukt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Finalisering mislukt");
    } finally {
      setManualBusy(false);
    }
  };

  const status = order?.betaal_status;
  const isDone = status === "betaald" || status === "mislukt";
  const showManualFallback = !isDone && (scopeMissing || polls >= MANUAL_FALLBACK_AFTER_POLLS);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-soft)] px-4"
      data-testid="sumup-solo-poll-screen"
    >
      <div className="w-full max-w-md tile p-8 text-center">
        <Smartphone className="w-12 h-12 mx-auto mb-2 text-[var(--ink-soft)]" />
        <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] mb-2">
          SumUp Solo terminal
        </div>

        {!isDone && (
          <div data-testid="solo-state-waiting">
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-[var(--brand-green)]" />
            <div className="text-2xl font-display font-extrabold">Wacht op klant…</div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">
              Klant tikt of insteekt kaart op Solo terminal
            </div>
            {order && (
              <div className="mt-3 text-lg">
                Order{" "}
                <span
                  className="px-2 py-1 rounded-lg text-white font-extrabold"
                  style={{ background: order.team_kleur }}
                >
                  {order.ordernummer}
                </span>{" "}
                · {fmtEUR(order.totaal)}
              </div>
            )}
          </div>
        )}

        {status === "betaald" && (
          <div data-testid="solo-state-success">
            <CheckCircle2 className="w-20 h-20 mx-auto mb-4 text-[var(--success)]" />
            <div className="text-3xl font-display font-extrabold text-[var(--success)]">
              Betaald!
            </div>
            {order && (
              <div className="mt-2 text-lg">
                Order{" "}
                <span
                  className="px-2 py-1 rounded-lg text-white font-extrabold"
                  style={{ background: order.team_kleur }}
                >
                  {order.ordernummer}
                </span>{" "}
                · {fmtEUR(order.totaal)}
              </div>
            )}
            {order?.sumup_manual_finalize && (
              <div className="text-xs text-[var(--ink-soft)] italic mt-2" data-testid="solo-manual-badge">
                Handmatig gefinaliseerd
              </div>
            )}
            {redirectIn != null && (
              <div className="text-sm text-[var(--ink-soft)] mt-3" data-testid="solo-redirect-countdown">
                Naar kassa in {redirectIn}…
              </div>
            )}
          </div>
        )}

        {status === "mislukt" && (
          <div data-testid="solo-state-failed">
            <XCircle className="w-20 h-20 mx-auto mb-4 text-[var(--danger)]" />
            <div className="text-2xl font-display font-extrabold text-[var(--danger)]">
              Niet gelukt
            </div>
            <div className="text-sm text-[var(--ink-soft)] mt-2">
              Probeer opnieuw of kies andere betaalmethode
            </div>
          </div>
        )}

        {showManualFallback && (
          <div className="mt-5 pt-5 border-t-2 border-dashed border-[var(--neutral)]" data-testid="solo-manual-fallback">
            <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)] font-bold mb-2">
              Handmatige finalisering
            </div>
            <p className="text-xs text-[var(--ink-soft)] mb-3">
              {scopeMissing
                ? "Automatische verificatie is niet beschikbaar. Kijk wat de Solo-terminal toont en kies hieronder:"
                : "Duurt het te lang? Kijk naar de Solo-terminal en bevestig zelf:"}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => manualFinalize("betaald")}
                disabled={manualBusy}
                className="btn-press inline-flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-[var(--success)] text-white font-bold disabled:opacity-50"
                data-testid="solo-manual-paid"
              >
                <Check className="w-4 h-4" /> Goedgekeurd
              </button>
              <button
                onClick={() => manualFinalize("mislukt")}
                disabled={manualBusy}
                className="btn-press inline-flex items-center justify-center gap-2 px-3 py-3 rounded-2xl bg-[var(--danger)] text-white font-bold disabled:opacity-50"
                data-testid="solo-manual-failed"
              >
                <XIcon className="w-4 h-4" /> Mislukt
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-center flex-wrap">
          {!isDone && (
            <button
              onClick={() => {
                setRefreshing(true);
                api.get(`/payments/sumup/solo/${orderId}/status`)
                  .then((r) => {
                    setOrder(r.data.order);
                    if (r.data.scope_missing) setScopeMissing(true);
                  })
                  .finally(() => setRefreshing(false));
              }}
              disabled={refreshing}
              className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[var(--brand-yellow)] text-[var(--ink)] font-bold disabled:opacity-50"
              data-testid="solo-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              Nu verversen
            </button>
          )}
          <button
            onClick={() => navigate("/kassa")}
            className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-[var(--ink)] text-white font-bold"
            data-testid="solo-back-to-kassa"
          >
            <ArrowLeft className="w-4 h-4" /> Naar kassa
          </button>
        </div>
      </div>
    </div>
  );
}
